import React, { type FunctionComponent, useCallback, useMemo, useRef } from 'react';
import classNames from 'classnames';
import createAccessibleOnClick, { BTN } from 'utils/createAccessibleOnClick';
import { Chevron } from 'components/Icons/Chevron';
import { useHoverDirty } from 'react-use';

import styles from './HoverMenu.module.scss';

interface MenuOption {
  name: string;
  value: any;
  selected?: boolean;
}

interface HoverMenuProps {
  onClick?: () => void;
  Icon?: React.ComponentType<{ isHovering: boolean }> | React.ReactNode;
  onClickMenuItem: (value: any) => void;
  menuTitle?: string;
  menuOptions: MenuOption[];
  label?: string;
  disabled?: boolean;
  useSelectOnMobile?: boolean;
  selectDescription?: string;
  defaultValue?: string;
}

const menuOptionClass = (selected: boolean) =>
  classNames({
    'quiver-hover-menu__menu-item': true,
    'quiver-hover-menu__menu-item--selected': selected,
  });

const getClass = (isHovering: boolean, useSelectOnMobile: boolean) =>
  classNames({
    'quiver-hover-menu': true,
    'quiver-hover-menu--hover': isHovering,
    'quiver-hover-menu--mobile-select': useSelectOnMobile,
  });

const HoverMenu: FunctionComponent<HoverMenuProps> = ({
  onClick,
  Icon,
  onClickMenuItem,
  menuTitle = '',
  menuOptions,
  label = '',
  disabled = false,
  useSelectOnMobile = false,
  selectDescription = '',
  defaultValue = '',
}) => {
  const hoverElementRef = useRef<HTMLButtonElement>(null);
  const isHovering = useHoverDirty(hoverElementRef);

  const renderedIcon = useMemo(() => {
    if (!Icon) return <Chevron direction="down" />;
    if (typeof Icon === 'function') return <Icon isHovering={isHovering} />;
    return Icon;
  }, [Icon, isHovering]);

  const handleSelect = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const { value } = event.target;
      const option = menuOptions.find(({ name }) => name === value);
      if (option) {
        onClickMenuItem(option.value);
      }
    },
    [menuOptions, onClickMenuItem],
  );

  return (
    <div className={styles.hoverMenu}>
      <button
        type="button"
        className={getClass(isHovering, useSelectOnMobile)}
        aria-haspopup={disabled ? undefined : 'menu'}
        aria-expanded={disabled ? undefined : isHovering}
        aria-label={menuTitle || label || 'Menu'}
        data-testid="hover-menu"
        disabled={disabled}
        ref={hoverElementRef}
        onClick={onClick}
      >
        <div className="quiver-hover-menu-target">
          {label && <h6 className="quiver-hover-menu-label">{label}</h6>}
          <div className="quiver-hover-menu-icon">{renderedIcon}</div>
          {useSelectOnMobile && (
            <select
              className="quiver-hover-menu-select"
              id={`quiver-hover-menu-select--${label}`}
              onChange={handleSelect}
              value={defaultValue}
            >
              {selectDescription && <option disabled>{selectDescription}</option>}
              {menuOptions.map(({ selected, name }) => (
                <option value={name} key={name} disabled={selected}>
                  {name}
                </option>
              ))}
            </select>
          )}
        </div>
        <ul role="menu" className="quiver-hover-menu__menu">
          {menuTitle && <h1 className="quiver-hover-menu__menu-title">{menuTitle}</h1>}
          {menuOptions.map(({ name, selected, value }) => (
            <li key={name} className="quiver-hover-menu__menu-item-container">
              <button
                type="button"
                className={menuOptionClass(!!selected)}
                disabled={!!selected}
                {...createAccessibleOnClick(() => onClickMenuItem(value), BTN, false, 'menuitem')}
              >
                {name}
              </button>
            </li>
          ))}
        </ul>
      </button>
    </div>
  );
};

export default HoverMenu;
